import { Loader2, PowerOff, Power, Trash2, X } from 'lucide-react';
import type { BulkCatalogAction } from '../../api';

interface BulkActionBarProps {
  selectedCount: number;
  /** Whether every visible row is currently selected (drives the header checkbox state). */
  allVisibleSelected: boolean;
  visibleCount: number;
  onToggleAllVisible: () => void;
  onAction: (action: BulkCatalogAction) => void;
  onClear: () => void;
  busyAction: BulkCatalogAction | null;
  /** Singular noun for copy, e.g. "product" */
  entityLabel: string;
  /** Plural noun; defaults to `${entityLabel}s` — pass "categories" etc. for irregular plurals */
  entityLabelPlural?: string;
}

/**
 * Sticky bulk-action row for catalog lists. Appears above the list; the
 * "select all" checkbox controls the currently visible (filtered) rows only.
 */
export function BulkActionBar({
  selectedCount,
  allVisibleSelected,
  visibleCount,
  onToggleAllVisible,
  onAction,
  onClear,
  busyAction,
  entityLabel,
  entityLabelPlural = `${entityLabel}s`,
}: BulkActionBarProps) {
  const busy = busyAction !== null;
  const hasSelection = selectedCount > 0;
  const plural = selectedCount === 1 ? entityLabel : entityLabelPlural;

  const actionBtn =
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2 rounded-lg border ${
        hasSelection ? 'bg-primary-50 border-primary-200' : 'bg-gray-50 border-gray-200'
      }`}
      data-testid="catalog-bulk-bar"
    >
      <label className="inline-flex items-center gap-2 text-sm text-gray-700 select-none cursor-pointer">
        <input
          type="checkbox"
          checked={allVisibleSelected && visibleCount > 0}
          disabled={visibleCount === 0 || busy}
          onChange={onToggleAllVisible}
          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          aria-label={allVisibleSelected ? 'Deselect all visible' : 'Select all visible'}
        />
        {hasSelection ? (
          <span>
            <span className="font-medium">{selectedCount}</span> {plural} selected
          </span>
        ) : (
          <span className="text-gray-500">
            Select {visibleCount > 0 ? 'all visible' : 'items'} for bulk actions
          </span>
        )}
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onAction('deactivate')}
          disabled={!hasSelection || busy}
          className={`${actionBtn} border-gray-200 bg-white text-gray-700 hover:bg-gray-50`}
          title="Hide from the complaint form. Reversible; historical complaints are unaffected."
        >
          {busyAction === 'deactivate' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <PowerOff className="w-4 h-4" />
          )}
          Deactivate
        </button>
        <button
          type="button"
          onClick={() => onAction('activate')}
          disabled={!hasSelection || busy}
          className={`${actionBtn} border-gray-200 bg-white text-gray-700 hover:bg-gray-50`}
          title="Make available in the complaint form again."
        >
          {busyAction === 'activate' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Power className="w-4 h-4" />
          )}
          Reactivate
        </button>
        <button
          type="button"
          onClick={() => onAction('delete')}
          disabled={!hasSelection || busy}
          className={`${actionBtn} border-red-200 bg-white text-red-600 hover:bg-red-50`}
          title="Permanently delete. Items used by any complaint are skipped automatically."
        >
          {busyAction === 'delete' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
          Delete
        </button>
        {hasSelection && (
          <button
            type="button"
            onClick={onClear}
            disabled={busy}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            aria-label="Clear selection"
            title="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
